import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Star, Trophy, Gift, ArrowUp, Coins, ShoppingBag, Tag, CircleNotch, Copy, Crown,
  Lightning, PencilSimple
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountLoyalty = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || 'mrg';
  const [loyalty, setLoyalty] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(-1);
  const [store, setStore] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API_URL}/api/s/${storeSlug}`).then(r => setStore(r.data)).catch(() => {});
  }, [storeSlug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [loyaltyResp, historyResp] = await Promise.all([
          axios.get(`${API_URL}/api/account/loyalty`, { withCredentials: true }),
          axios.get(`${API_URL}/api/account/loyalty/history`, { withCredentials: true }),
        ]);
        setLoyalty(loyaltyResp.data);
        setHistory(historyResp.data);
      } catch { }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleRedeem = async (index) => {
    setRedeeming(index);
    try {
      const resp = await axios.post(`${API_URL}/api/account/loyalty/redeem`, { reward_index: index }, { withCredentials: true });
      toast.success(resp.data.message);
      const [loyaltyResp, historyResp] = await Promise.all([
        axios.get(`${API_URL}/api/account/loyalty`, { withCredentials: true }),
        axios.get(`${API_URL}/api/account/loyalty/history`, { withCredentials: true }),
      ]);
      setLoyalty(loyaltyResp.data);
      setHistory(historyResp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setRedeeming(-1); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code promo copie !');
  };

  const colors = { primary: store?.identity?.primary_color || '#c2185b' };
  const tier = loyalty?.tier || {};
  const nextTier = loyalty?.next_tier;

  if (loading) return (
    <div className="py-12 text-center">
      <div className="animate-spin rounded-full h-7 w-7 border-b-2 mx-auto" style={{ borderColor: colors.primary }} />
    </div>
  );

  if (!loyalty?.enabled) return (
    <Card className="border-0 shadow-sm">
      <CardContent className="py-12 text-center">
        <Star size={48} className="mx-auto text-slate-200 mb-4" />
        <h3 className="text-lg font-semibold">Programme de fidelite</h3>
        <p className="text-slate-400 text-sm mt-1">Le programme n'est pas encore disponible</p>
      </CardContent>
    </Card>
  );

  const progressPercent = nextTier
    ? ((loyalty.total_earned - (tier.min_points || 0)) / (nextTier.min_points - (tier.min_points || 0))) * 100
    : 100;

  const filteredHistory = historyFilter === 'all'
    ? history
    : history.filter(h => h.type === historyFilter);

  return (
    <div className="space-y-5" data-testid="account-loyalty">
      <div>
        <h2 className="text-xl font-bold">Mon programme fidelite</h2>
        <p className="text-slate-400 text-[13px] mt-0.5">Cumulez des points a chaque achat et echangez-les contre des recompenses</p>
      </div>

      {/* Active Multiplier Banner */}
      {loyalty.active_multiplier > 1 && (
        <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl" data-testid="multiplier-banner">
          <Lightning size={24} weight="fill" className="text-amber-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-800">Points x{loyalty.active_multiplier} en cours !</p>
            <p className="text-xs text-amber-600">Vos achats rapportent {loyalty.active_multiplier}x plus de points pendant cette periode</p>
          </div>
        </div>
      )}

      {/* Points & Tier Card */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white relative overflow-hidden" data-testid="loyalty-main-card">
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10" style={{ backgroundColor: tier.color || colors.primary, filter: 'blur(40px)' }} />
        
        <div className="flex items-start justify-between relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Crown size={18} weight="fill" style={{ color: tier.color || '#FFD700' }} />
              <span className="text-sm font-bold" style={{ color: tier.color || '#FFD700' }}>{tier.name}</span>
              {tier.discount_percent > 0 && (
                <Badge className="bg-white/10 text-white/80 text-[10px]">-{tier.discount_percent}% permanent</Badge>
              )}
            </div>
            <div className="flex items-baseline gap-1 mt-3">
              <span className="text-4xl font-black" data-testid="loyalty-points">{loyalty.points}</span>
              <span className="text-white/50 text-sm">points</span>
            </div>
            <p className="text-white/40 text-xs mt-1">
              {loyalty.points_per_euro} point{loyalty.points_per_euro > 1 ? 's' : ''} par euro depense
              {loyalty.active_multiplier > 1 && (
                <span className="text-amber-400 ml-1">(x{loyalty.active_multiplier} bonus actif)</span>
              )}
            </p>
          </div>
          
          <div className="text-right">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2" style={{ backgroundColor: tier.color || colors.primary, opacity: 0.85 }}>
              <Trophy size={28} weight="fill" className="text-white" />
            </div>
            <p className="text-[11px] text-white/50">{loyalty.total_orders} commande(s)</p>
            <p className="text-[11px] text-white/50">{(loyalty.total_order_amount || 0).toFixed(0)} EUR d'achats</p>
          </div>
        </div>

        {/* Progress to next tier */}
        {nextTier && (
          <div className="mt-5 relative z-10">
            <div className="flex justify-between text-[11px] mb-1.5">
              <span className="text-white/50">{tier.name}</span>
              <span style={{ color: nextTier.color }} className="font-semibold">{nextTier.name}</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.min(progressPercent, 100)}%`, backgroundColor: nextTier.color }}
              />
            </div>
            <p className="text-[10px] text-white/40 mt-1.5 flex items-center gap-1">
              <ArrowUp size={10} /> Encore <strong className="text-white/70">{loyalty.points_to_next_tier} pts</strong> pour atteindre {nextTier.name} ({nextTier.discount_percent}% de remise)
            </p>
          </div>
        )}
      </div>

      {/* Rewards */}
      <div>
        <h3 className="text-[15px] font-bold mb-3 flex items-center gap-2">
          <Gift size={18} style={{ color: colors.primary }} /> Recompenses disponibles
        </h3>
        <div className="grid gap-3 sm:grid-cols-3">
          {loyalty.rewards?.map((reward, i) => {
            const canAfford = loyalty.points >= reward.points_cost;
            return (
              <Card key={i} className={`border-0 shadow-sm transition-all ${canAfford ? 'hover:shadow-md' : 'opacity-60'}`} data-testid={`reward-${i}`}>
                <CardContent className="p-4 text-center">
                  <div className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center"
                    style={{ backgroundColor: canAfford ? colors.primary : '#e2e8f0', color: canAfford ? '#fff' : '#94a3b8' }}>
                    {reward.type === 'free_shipping' ? <ShoppingBag size={18} weight="fill" /> : <Tag size={18} weight="fill" />}
                  </div>
                  <p className="text-[13px] font-semibold">{reward.name}</p>
                  <p className="text-[12px] font-bold mt-1" style={{ color: canAfford ? colors.primary : '#94a3b8' }}>
                    <Coins size={12} weight="fill" className="inline mr-0.5" />{reward.points_cost} pts
                  </p>
                  <Button
                    size="sm"
                    disabled={!canAfford || redeeming === i}
                    className="mt-3 w-full text-[11px] h-8 text-white"
                    style={{ backgroundColor: canAfford ? colors.primary : '#cbd5e1' }}
                    onClick={() => handleRedeem(i)}
                    data-testid={`redeem-${i}`}
                  >
                    {redeeming === i ? <CircleNotch size={14} className="animate-spin" /> : 'Echanger'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* History with Filter */}
      {history.length > 0 && (
        <div data-testid="loyalty-history">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[15px] font-bold">Historique des points</h3>
            <div className="flex gap-1">
              {[
                { key: 'all', label: 'Tout' },
                { key: 'earn', label: 'Gagnes' },
                { key: 'redeem', label: 'Echanges' },
                { key: 'admin_adjust', label: 'Ajustements' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setHistoryFilter(f.key)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                    historyFilter === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`history-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {filteredHistory.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between px-4 py-3" data-testid={`history-${entry.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        entry.type === 'earn' ? 'bg-emerald-100' : entry.type === 'admin_adjust' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        {entry.type === 'earn' 
                          ? <ArrowUp size={14} className="text-emerald-600" weight="bold" />
                          : entry.type === 'admin_adjust'
                          ? <PencilSimple size={14} className="text-blue-600" weight="bold" />
                          : <Gift size={14} className="text-amber-600" weight="fill" />
                        }
                      </div>
                      <div>
                        <p className="text-[13px] font-medium">{entry.description}</p>
                        <p className="text-[10px] text-slate-400">
                          {new Date(entry.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {entry.multiplier > 1 && <span className="ml-1 text-amber-500">(x{entry.multiplier})</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[14px] font-bold ${entry.points > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {entry.points > 0 ? '+' : ''}{entry.points} pts
                      </span>
                      {entry.promo_code && (
                        <button onClick={() => copyCode(entry.promo_code)} className="text-slate-400 hover:text-slate-700" data-testid={`copy-promo-${entry.id}`}>
                          <Copy size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {filteredHistory.length === 0 && (
                  <p className="text-center text-slate-400 py-6 text-sm">Aucun resultat pour ce filtre</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AccountLoyalty;
